import { useState, useRef, useContext } from 'react';
import moment from "moment";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import airports from "../../Data/AirportList.json";
import { useNavigate } from 'react-router-dom';
import './Flight.css';
import DefaultData from '../../CompanyData/DefaultData';
import { LandingContext } from '../Profile/LandingContext';

const FlightSearchPanel = () => {
    const navigate    = useNavigate();
    const landingData = useContext(LandingContext);

    // ── State ──────────────────────────────────────────────
    const [departureDate, setDepartureDate] = useState(new Date());
    const [returnDate, setReturnDate]       = useState(new Date());
    const [departureResults, setDepartureResults] = useState([]);
    const [arrivalResults, setArrivalResults]     = useState([]);
    const [locationFrom, setLocationFrom] = useState('HYD');
    const [locationTo, setLocationTo]     = useState('BOM');
    const [displayFrom, setDisplayFrom]   = useState('Hyderabad(HYD), India');
    const [displayTo, setDisplayTo]       = useState('Mumbai(BOM), India');
    const [selectedDeparture, setSelectedDeparture] = useState(null);
    const [selectedArrival, setSelectedArrival]     = useState(null);
    const [cityFrom, setCityFrom] = useState('Hyderabad');
    const [cityTo, setCityTo]     = useState('Mumbai');
    const [from, setFrom] = useState('Hyderabad, India');
    const [to, setTo]     = useState('Mumbai, India');
    const [trips] = useState([
        { from: "HYD, India", to: "BOM, India", locationFrom: "HYD", locationTo: "BOM", displayFrom: "Hyderabad(HYD)", displayTo: "Mumbai(BOM)", cityFrom: "Hyderabad", cityTo: "Mumbai", departureDate: new Date(), departureResults: [], arrivalResults: [] },
    ]);
    const [adults, setAdults]     = useState(1);
    const [kids, setKids]         = useState(0);
    const [infants, setInfants]   = useState(0);
    const [cabinClass, setCabinClass] = useState("ECONOMY");
    const [preffered, setPreferred]   = useState("REGULAR");
    const [isDirect, setIsDirect]         = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [errors, setErrors]   = useState({});
    const [isDomestic, setIsDomestic] = useState('DOMESTIC');
    const [journeyType, setJourneyType] = useState("ONEWAY");

    const inputDRef = useRef(null);
    const inputARef = useRef(null);

    // ── Validation ─────────────────────────────────────────
    const validate = () => {
        const temp = { from: from !== "", to: to !== "" };
        setErrors(temp);
        return Object.values(temp).every(Boolean);
    };

    // ── FROM handlers ──────────────────────────────────────
    const handleDepartureAirportSearch = (e) => {
        const value = e.target.value;
        setDisplayFrom(value);
        if (value.length > 0) {
            const lv = value.toLowerCase();
            let filtered = airports.filter(a => a.display === true && a.airportCode.toLowerCase().includes(lv)).slice(0, 10);
            if (!filtered.length) filtered = airports.filter(a => a.airportCode.toLowerCase().includes(lv) && (!selectedArrival || a.airportCode !== selectedArrival.airportCode)).slice(0, 10);
            setDepartureResults(filtered);
        } else {
            setDepartureResults([]);
        }
    };
    const handleDepartureAirportClick = () => {
        setArrivalResults([]);
        setDepartureResults(airports.filter(a => a.display === true).slice(0, 10));
        inputDRef.current?.select();
    };
    const handleDepartureSelect = (data) => {
        if (selectedArrival && selectedArrival.airportCode === data.airportCode) {
            alert("Departure and arrival airports cannot be the same.");
            setSelectedArrival(null); setDisplayTo("");
            return;
        }
        if (data.countryCode !== "IN") setIsDomestic("INTERNATIONAL");
        setDisplayFrom(`${data.cityName}(${data.airportCode}), ${data.countryName}`);
        setLocationFrom(data.airportCode); setCityFrom(data.cityName); setFrom(data.cityName);
        setSelectedDeparture(data); setDepartureResults([]);
    };

    // ── TO handlers ────────────────────────────────────────
    const handleArrivalAirportSearch = (e) => {
        const value = e.target.value;
        setDisplayTo(value);
        if (value.length > 0) {
            const lv = value.toLowerCase();
            let filtered = airports.filter(a => a.airportCode.toLowerCase().includes(lv)).slice(0, 10);
            if (!filtered.length) filtered = airports.filter(a => a.cityName.toLowerCase().includes(lv)).slice(0, 10);
            setArrivalResults(filtered);
        } else {
            setArrivalResults([]);
        }
    };
    const handleArrivalAirportClick = () => {
        setDepartureResults([]);
        setArrivalResults(airports.filter(a => a.display === true).slice(0, 10));
        inputARef.current?.select();
    };
    const handleArrivalSelect = (data) => {
        if (selectedDeparture && selectedDeparture.airportCode === data.airportCode) {
            alert("Arrival and departure airports cannot be the same.");
            setSelectedDeparture(null); setDisplayFrom("");
            return;
        }
        if (data.countryCode !== "IN") setIsDomestic("INTERNATIONAL");
        setDisplayTo(`${data.cityName}(${data.airportCode}), ${data.countryName}`);
        setLocationTo(data.airportCode); setCityTo(data.cityName); setTo(data.cityName);
        setSelectedArrival(data); setArrivalResults([]);
    };

    // ── Swap FROM ↔ TO ────────────────────────────────────
    const handleSwap = () => {
        setDisplayFrom(displayTo); setDisplayTo(displayFrom);
        setLocationFrom(locationTo); setLocationTo(locationFrom);
        setCityFrom(cityTo); setCityTo(cityFrom);
        setFrom(to); setTo(from);
        setSelectedDeparture(selectedArrival); setSelectedArrival(selectedDeparture);
    };

    // ── Date handlers ──────────────────────────────────────
    const handleDepartureDate = (date) => {
        const d = date[0];
        setDepartureDate(d);
        if (returnDate && d && returnDate < d) setReturnDate(d);
    };
    const handleReturnDate = (date) => setReturnDate(date[0]);

    // ── Journey type ───────────────────────────────────────
    const handleJourneyType = (e, type) => { e.preventDefault(); setJourneyType(type); };

    // ── Pax handlers ───────────────────────────────────────
    const handleIncreaseAdults   = (e) => { e.stopPropagation(); setAdults(a => a + 1); };
    const handleDecreaseAdults   = (e) => { e.stopPropagation(); setAdults(a => Math.max(1, a - 1)); };
    const handleIncreaseKids     = (e) => { e.stopPropagation(); setKids(k => k + 1); };
    const handleDecreaseKids     = (e) => { e.stopPropagation(); setKids(k => Math.max(0, k - 1)); };
    const handleIncreaseInfants  = (e) => { e.stopPropagation(); setInfants(i => i + 1); };
    const handleDecreaseInfants  = (e) => { e.stopPropagation(); setInfants(i => Math.max(0, i - 1)); };

    // ── Search ─────────────────────────────────────────────
    const handleSearch = (e) => {
        e.preventDefault();
        if (!validate()) return;
        const searchQuery = {
            journeyType, isDomestic, adults, kids, infants, cabinClass,
            companyId: DefaultData.DEFAULTCOMPANYID,
            preferred: preffered,
            isDirectFlight: isDirect,
            isConnectingFlight: isConnecting,
            flightTravelerPoints: trips.map(() => ({
                from: locationFrom, to: locationTo,
                departureDate: moment(departureDate).format('YYYY-MM-DD'),
                returnDate: moment(returnDate).format('YYYY-MM-DD'),
            })),
        };
        navigate(`/flight/${adults}/${kids}/${infants}/${isDomestic}`, { state: { searchQuery } });
    };

    // ── Pax summary label ──────────────────────────────────
    const paxSummary = `${adults} Adult${adults > 1 ? 's' : ''}${kids ? `, ${kids} Kid${kids > 1 ? 's' : ''}` : ''}${infants ? `, ${infants} Infant${infants > 1 ? 's' : ''}` : ''} · ${cabinClass.replace('_', ' ')}`;

    // ══════════════════════════════════════════════════════
    return (
        <div className="search-flight-panel-container">

            {/* ── Row 1: tabs + quick-filter chips ─────── */}
            <div className="sfp-header">
                <div className="sfp-tabs">
                    <button
                        className={`sfp-tab${journeyType === "ONEWAY" ? " active" : ""}`}
                        onClick={(e) => handleJourneyType(e, "ONEWAY")}
                    >
                        <i className="ri-arrow-right-line" /> One Way
                    </button>
                    <button
                        className={`sfp-tab${journeyType === "RETURN" ? " active" : ""}`}
                        onClick={(e) => handleJourneyType(e, "RETURN")}
                    >
                        <i className="ri-repeat-line" /> Round Trip
                    </button>
                </div>

                <div className="sfp-filters">
                    {["REGULAR", "STUDENT", "CORPORATE"].map(type => (
                        <label key={type} className={`sfp-radio-chip${preffered === type ? " checked" : ""}`}>
                            <input type="radio" name="preffered" value={type} checked={preffered === type} onChange={(e) => setPreferred(e.target.value)} />
                            {type.charAt(0) + type.slice(1).toLowerCase()}
                        </label>
                    ))}
                    <label className={`sfp-radio-chip${isDirect ? " checked" : ""}`}>
                        <input type="checkbox" checked={isDirect} onChange={(e) => setIsDirect(e.target.checked)} />
                        <i className="ri-flight-takeoff-line" /> Direct
                    </label>
                    <label className={`sfp-radio-chip${isConnecting ? " checked" : ""}`}>
                        <input type="checkbox" checked={isConnecting} onChange={(e) => setIsConnecting(e.target.checked)} />
                        <i className="ri-node-tree" /> Connecting
                    </label>
                </div>
            </div>

            {/* ── Row 2: main search fields ─────────────── */}
            <div className="row g-3 align-items-end">

                {/* FROM */}
                <div className="col-lg col-md-6" style={{ position: 'relative' }}>
                    <label className="sfp-label">
                        <i className="ri-flight-takeoff-line me-1" /> From
                    </label>
                    <div className="sfp-input-wrap">
                        <i className="ri-map-pin-line sfp-icon" />
                        <input
                            ref={inputDRef}
                            className="form-control"
                            type="text"
                            placeholder="City or airport"
                            value={displayFrom}
                            onChange={handleDepartureAirportSearch}
                            onClick={handleDepartureAirportClick}
                            onFocus={handleDepartureAirportClick}
                            autoComplete="off"
                        />
                    </div>
                    {errors.from === false && (
                        <div className="sfp-error"><i className="ri-error-warning-line" /> Select departure airport</div>
                    )}
                    {departureResults.length > 0 && (
                        <div className="sfp-airport-list">
                            {departureResults.map(airport => (
                                <div className="sfp-airport-item" key={airport.airportCode} onClick={() => handleDepartureSelect(airport)}>
                                    <div className="sfp-airport-icon"><i className="ri-flight-takeoff-line" /></div>
                                    <div>
                                        <div className="sfp-airport-main">{airport.cityName} ({airport.airportCode})</div>
                                        <div className="sfp-airport-sub">{airport.airportName}, {airport.countryName}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Swap button */}
                <div className="col-auto d-flex align-items-end pb-1">
                    <button
                        type="button"
                        onClick={handleSwap}
                        title="Swap airports"
                        style={{
                            width: 36, height: 36, borderRadius: '50%',
                            background: 'rgba(255,255,255,0.18)', border: '1.5px solid rgba(255,255,255,0.35)',
                            color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '1rem', transition: 'all 0.2s', flexShrink: 0,
                        }}
                        onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.32)'}
                        onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
                    >
                        <i className="ri-arrow-left-right-line" />
                    </button>
                </div>

                {/* TO */}
                <div className="col-lg col-md-6" style={{ position: 'relative' }}>
                    <label className="sfp-label">
                        <i className="ri-flight-land-line me-1" /> To
                    </label>
                    <div className="sfp-input-wrap">
                        <i className="ri-map-pin-2-line sfp-icon" style={{ color: '#C8A84B' }} />
                        <input
                            ref={inputARef}
                            className="form-control"
                            type="text"
                            placeholder="City or airport"
                            value={displayTo}
                            onChange={handleArrivalAirportSearch}
                            onClick={handleArrivalAirportClick}
                            onFocus={handleArrivalAirportClick}
                            autoComplete="off"
                        />
                    </div>
                    {errors.to === false && (
                        <div className="sfp-error"><i className="ri-error-warning-line" /> Select arrival airport</div>
                    )}
                    {arrivalResults.length > 0 && (
                        <div className="sfp-airport-list">
                            {arrivalResults.map(airport => (
                                <div className="sfp-airport-item" key={airport.airportCode} onClick={() => handleArrivalSelect(airport)}>
                                    <div className="sfp-airport-icon" style={{ background: 'rgba(200,168,75,0.1)', color: '#C8A84B' }}>
                                        <i className="ri-flight-land-line" />
                                    </div>
                                    <div>
                                        <div className="sfp-airport-main">{airport.cityName} ({airport.airportCode})</div>
                                        <div className="sfp-airport-sub">{airport.airportName}, {airport.countryName}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Departure Date */}
                <div className="col-lg col-md-6">
                    <label className="sfp-label"><i className="ri-calendar-event-line me-1" /> Departure</label>
                    <div className="sfp-date-wrap">
                        <Flatpickr
                            value={departureDate}
                            onChange={handleDepartureDate}
                            options={{ dateFormat: "d M Y", minDate: "today" }}
                            className="form-control"
                            placeholder="Select date"
                        />
                        <i className="ri-calendar-2-line sfp-date-icon" />
                    </div>
                </div>

                {/* Return Date */}
                {journeyType === "RETURN" && (
                    <div className="col-lg col-md-6">
                        <label className="sfp-label"><i className="ri-calendar-check-line me-1" /> Return</label>
                        <div className="sfp-date-wrap">
                            <Flatpickr
                                value={returnDate}
                                onChange={handleReturnDate}
                                options={{ dateFormat: "d M Y", minDate: departureDate }}
                                className="form-control"
                                placeholder="Select date"
                            />
                            <i className="ri-calendar-2-line sfp-date-icon" />
                        </div>
                    </div>
                )}

                {/* Travellers & Class */}
                <div className="col-lg-3 col-md-6">
                    <label className="sfp-label"><i className="ri-group-line me-1" /> Travellers &amp; Class</label>
                    <div className="dropdown" data-bs-auto-close="outside">
                        <button
                            className="sfp-pax-trigger dropdown-toggle"
                            type="button"
                            id="sfpPaxDropdown"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                        >
                            <i className="ri-user-3-line" />
                            <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                                {paxSummary}
                            </span>
                            <i className="ri-arrow-down-s-line sfp-pax-caret" />
                        </button>

                        <div className="dropdown-menu sfp-pax-menu" aria-labelledby="sfpPaxDropdown">
                            {/* Adults */}
                            <div className="sfp-pax-row">
                                <div className="sfp-pax-info">
                                    <span className="sfp-pax-name">Adults</span>
                                    <span className="sfp-pax-sub">12 years &amp; above</span>
                                </div>
                                <div className="sfp-counter">
                                    <button type="button" onClick={handleDecreaseAdults} disabled={adults <= 1}>−</button>
                                    <input type="number" readOnly value={adults} />
                                    <button type="button" onClick={handleIncreaseAdults}>+</button>
                                </div>
                            </div>
                            {/* Kids */}
                            <div className="sfp-pax-row">
                                <div className="sfp-pax-info">
                                    <span className="sfp-pax-name">Children</span>
                                    <span className="sfp-pax-sub">2 – 11 years</span>
                                </div>
                                <div className="sfp-counter">
                                    <button type="button" onClick={handleDecreaseKids} disabled={kids <= 0}>−</button>
                                    <input type="number" readOnly value={kids} />
                                    <button type="button" onClick={handleIncreaseKids}>+</button>
                                </div>
                            </div>
                            {/* Infants */}
                            <div className="sfp-pax-row">
                                <div className="sfp-pax-info">
                                    <span className="sfp-pax-name">Infants</span>
                                    <span className="sfp-pax-sub">Under 2 years</span>
                                </div>
                                <div className="sfp-counter">
                                    <button type="button" onClick={handleDecreaseInfants} disabled={infants <= 0}>−</button>
                                    <input type="number" readOnly value={infants} />
                                    <button type="button" onClick={handleIncreaseInfants}>+</button>
                                </div>
                            </div>
                            {/* Cabin class */}
                            <div className="sfp-cabin-select">
                                <select className="form-select" value={cabinClass} onChange={(e) => setCabinClass(e.target.value)}>
                                    <option value="ECONOMY">Economy</option>
                                    <option value="PREMIUM_ECONOMY">Premium Economy</option>
                                    <option value="BUSINESS">Business</option>
                                    <option value="FIRST">First Class</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Search button */}
                <div className="col-lg-auto col-md-12">
                    <button type="button" className="sfp-search-btn" onClick={handleSearch}>
                        <i className="ri-search-line" /> Search Flights
                    </button>
                </div>

            </div>{/* end row */}
        </div>
    );
};

export default FlightSearchPanel;
